import React from "react";
// import { toast } from "react-toastify";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { dateHelpers, constants, storage } from "./";

const changeDateFormat = (date, changeDateFunction, fieldName = null, itemToEditId = null) => {
   let formattedDate = null;
   if (dateHelpers.isDateValid(date)) {
      const dateString = dateHelpers.parseDatePickerDate(date, dateHelpers.MDY4);
      formattedDate = dateHelpers.getMomentFromString(dateString);
   }
   if (fieldName && itemToEditId) {
      changeDateFunction(formattedDate, fieldName, itemToEditId);
   } else if (fieldName) {
      changeDateFunction(formattedDate, fieldName);
   } else if (itemToEditId) {
      changeDateFunction(formattedDate, itemToEditId);
   } else {
      changeDateFunction(formattedDate);
   }
};

function getLocalStorageUser() {
   const u = storage.getItem("currentUser");
   return u ? JSON.parse(u) : null;
}

const hasRoleId = (typeOfUserRole, currentUser = null) => {
   const user = currentUser ? currentUser : getLocalStorageUser();
   const userRoles = user ? user.roles : null;
   return (userRoles && _.some(userRoles, (ur) => ur.typeOfUserRole === typeOfUserRole));
};

const helpers = {
   // addCommasToString(string) {
   //   const numberString = string;
   //   let formattedString = "{:,}".format(int(numberString));
   //   return formattedString;
   // },

   // The customer account currently in effect for a CUSTOMER-role user: the picker-selected active
   // account, falling back to their primary mirror when nothing is selected yet. Single source of truth
   // so the ~13 sites that used to read currentUser.customerId directly (which silently meant "the
   // primary", wrong once a customer has more than one account) can't drift. Call ONLY in a customer-role
   // branch - internal users scope by a passed-in customerId/customerFilter, not by this.
   activeCustomerId(userCtx) {
      return userCtx?.customerFilter?.value ?? userCtx?.currentUser?.customerId;
   },

   addLabelDepartmentList(list) {
      if (!list || !list.length) return [];
      const valueLabeledList = _.chain(list)
         .orderBy((x) => [x.deptName])
         .map((x) => {
            x.label = x.deptName;
            x.value = x.deptId;
            return x;
         })
         .value();
      return valueLabeledList;
   },

   addLabelProcessStepList(list) {
      // Used by ManageMsrProcesses.js
      if (!list || !list.length) return [];
      const valueLabeledList = _.chain(list)
         .orderBy((x) => [x.step])
         .map((x) => {
            x.label = x.process;
            x.value = x.id;   // Primary Key on the table
            return x;
         })
         .value();
      return valueLabeledList;
   },

   addLabelValueToGLA(list) {
      if (!list || !list.length) return [];
      const valueLabeledList = _.map(list, (x) => {
         x.label = `${x.accountNumber} | ${x.accountDescription} `;
         x.value = x.accountIndex;
         return x;
      });
      return valueLabeledList;
   },

   addLabelValueToItem(list) {
      if (!list || !list.length) return [];
      const valueLabeledList = _.map(list, (x) => {
         x.label = x.itemClassWithDesc;
         x.value = x.id;
         return x;
      });
      return valueLabeledList;
   },

   addLabelValueToList(list, propName = null) {
      if (!list || !list.length || list.length === 0) return [];
      const valueLabelList = _.chain(list)
         .map((x) => {
            x.label = x.name;
            x.value = x.id;
            return x;
         })
         .sortBy((x) => { return x.label; })
         .value();
      return propName ? { [propName]: valueLabelList, } : valueLabelList;
   },

   addLabelValueToMachineSetupDto(list) {
      if (!list || !list.length) return [];
      const valueLabeledList = _.chain(list)
         // .filter((x) => x.isActive)
         .map((x) => {
            x.label = x.name;
            x.value = x.id;
            return x;
         })
         .value();
      return valueLabeledList;
   },

   addLabelValueToMachineTypeDto(list) {
      if (!list || !list.length) return [];
      const valueLabeledList = _.chain(list)
         .filter((x) => x.isActive)
         .map((x) => {
            x.label = x.typeName;
            x.value = x.typeId;
            x.machines = x.activeMachines;
            return x;
         })
         .value();
      return valueLabeledList;
   },

   addLabelValueToRwoTypeUnfiltered(list) {
      if (!list || !list.length) return [];
      const valueLabeledList = _.chain(list)
         .map((x) => {
            x.label = x.typeName;
            x.value = x.typeId;
            x.machines = x.activeMachines;
            return x;
         })
         .value();
      return valueLabeledList;
   },

   alertMinQty(itemNumber, minQty, baseUnit) {
      if (baseUnit > 1)
         toast.warning(`The minimum quantity for ${itemNumber} is ${minQty}. Anything larger must be in increments of ${baseUnit}. Please adjust`);
      else
         toast.warning(`The minimum quantity for ${itemNumber} is ${minQty}. Please adjust`);
   },

   booleanToYesNo: function (b) {
      return b ? "Yes" : "No";
   },

   // Whether to SHOW cost, margin and markup columns - the client half of AppHelpers.CanSeeCostAndMargin (the
   // server blanks the values for everyone else). A user with any customer role never sees them. Hide, never disable.
   canSeeCostAndMargin(currentUser) {
      if (!currentUser || currentUser.isAnyCustomerTypeRole) return false;
      return _.some(currentUser.roles, (r) => _.includes(constants.COST_AND_MARGIN_ROLE_IDS, r.typeOfUserRole));
   },

   commify(n) {
      if (n) {
         var parts = n.toString().split(".");
         const numberPart = parts[0];
         const decimalPart = parts[1];
         const thousands = /\B(?=(\d{3})+(?!\d))/g;
         return (numberPart.replace(thousands, ",") + (decimalPart ? "." + decimalPart : ""));
      }
      return n;
   },

   downloadExport: function (data, fileName, mime) {
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
         window.navigator.msSaveOrOpenBlob(data, fileName);
      } else {
         const file = new Blob([data], { type: mime });
         var anchor = window.document.createElement("a");
         anchor.download = fileName;
         anchor.href = (window.webkitURL || window.URL).createObjectURL(file);
         anchor.dataset.downloadurl = [mime, anchor.download, anchor.href].join(":");
         anchor.click();
      }
   },

   emailIsValid: function (email) {
      // eslint-disable-next-line no-useless-escape
      var emailRe = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return emailRe.test(email);
   },

   formatCurrency(value, places = 2) {
      if (!value) value = 0;
      if (typeof value === "string") {
         if (helpers.textContains(value, "$"))
            value = value.slice(1);
         value = parseFloat(value);
      }
      return new Intl.NumberFormat("en-US", {
         style: "currency",
         currency: "USD",
         minimumFractionDigits: places,
      }).format(value);
   },

   formatNumberWithThousands(number, fractionalDigits = 0) {
      if (_.isNil(number)) {
         // toast.info("A number passed to a formatting function was null");
         return 0;
      }
      return number.toLocaleString("en-US", { maximumFractionDigits: fractionalDigits, });
   },

   formatPercent(value, places = 0) {
      return new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: places, }).format(value);
   },

   formatPhoneNumber(phoneNumberString) {
      if (!phoneNumberString) return "";
      phoneNumberString = phoneNumberString.replace(/[^\d]+/g, "");
      if (phoneNumberString.length === 7) {
         phoneNumberString = phoneNumberString.replace(/(\d{3})(\d{4})/, "$1-$2");
      } else if (phoneNumberString.length === 10) {
         phoneNumberString = phoneNumberString.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
      } else if (phoneNumberString.length === 11) {
         phoneNumberString = phoneNumberString.replace(/^(1|)?(\d{3})(\d{3})(\d{4})/, "($2) $3-$4");
      }
      return phoneNumberString;
   },

   formatWithDecimalPlaces(value, places = 2) {
      return new Intl.NumberFormat("en-US", { minimumFractionDigits: places, }).format(value);
   },

   getArrayOfStrings(list) {
      let result = [];
      if (!_.isArray(list) || _.isEmpty(list)) return result;
      _.map(list, (x) => {
         if (typeof x.value === "string") { result.push(x.value.trim()); }
      });
      return result;
   },

   getCommaDelimitedString(list) {
      let result = "";
      if (!_.isArray(list) || _.isEmpty(list)) return result;
      _.map(list, (x) => {
         if (typeof x.value === "string") { result += `${x.value.trim()},`; }
      });
      result = result.slice(0, -1);
      return result;
   },

   getCurrentUser() {
      const userString = storage.getItem("currentUser");
      return userString ? JSON.parse(userString) : null;
   },

   hasAdminRole(roles) {
      for (let i = 0; i < roles.length; i++) {
         if (roles[i] === 1) {
            return true;
         }
      }
      return false;
   },

   hasAnyRoleId(currentUser, roleList) {
      if (!roleList || !roleList.length) return false;
      const user = currentUser ? currentUser : getLocalStorageUser();
      const userRoles = user ? user.roles : null;
      return userRoles && _.some(roleList, (role) => hasRoleId(role, user));
   },

   // A signed-in account with no roles at all - neither staff nor customer. The server's
   // RoleRequiredFilter answers 403 to every non-anonymous action for such an account, so the shell
   // shows it the home page only and must not start any call. False when there is no user, so the
   // signed-out paths keep their own handling.
   hasNoRoles(user) {
      return !!user && _.isEmpty(user.roleTypeIdList);
   },

   hasRole(userRoles, roleList) {
      if (_.isEmpty(roleList)) return true;
      return _.some(userRoles, (item) => _.includes(roleList, item));
   },

   hasRoleId,

   idNameToValueLabel(list, propName = null) {
      if (!list || !list.length || list.length === 0) return [];
      const valueLabelList = _.chain(list)
         .map((x) => ({
            label: x.name,
            value: x.id,
         }))
         .sortBy((x) => { return x.label; })
         .value();
      return propName ? { [propName]: valueLabelList } : valueLabelList;
   },

   isMobile: function (window) {
      const smallerOption =
         window.document &&
            window.document.defaultView &&
            window.document.defaultView.innerWidth
            ? window.document.defaultView.innerWidth
            : window.screen.width;
      return smallerOption <= 680;
   },

   isMultiple(num, divisor) {
      //-----------------------------------------
      // Checks if num is a multiple of divisor
      // num: the number to check
      // divisor: the number to divide by
      // returns true if num is a multiple of divisor, otherwise false
      //-----------------------------------------
      // Validate inputs
      if (typeof num !== 'number') {
         num = parseInt(num, 10);
      }
      if (typeof divisor !== 'number') {
         divisor = parseInt(divisor, 10);
      }
      if (typeof num !== 'number' || typeof divisor !== 'number' || !Number.isFinite(num) || !Number.isFinite(divisor)) {
         throw new Error('Both arguments must be finite numbers.');
      }

      // Division by zero check
      if (divisor === 0) {
         return false; // No number is a multiple of zero except zero itself
      }

      return num % divisor === 0;
   },

   isNumber(value) {
      // Is the value supplied a number - negative or positive
      return /^[-]?\d+$/.test(value);
   },

   mustChangePassword: function (user) {
      if (!user) return false;
      return user.mustChangePassword;
   },

   onDatePickerKeyDown(event, changeDateFunction, fieldName = null, itemToEditId = null) {
      if (event.which === 9 || event.which === 13) {
         // tab key or enter key
         const eventAction = event && event.target ? event.target.value : null;
         changeDateFormat(eventAction, changeDateFunction, fieldName, itemToEditId);
      }
   },

   percentChange(value1, value2) {
      let subTotal = 0;
      if (typeof value1 !== "number" || typeof value2 !== "number") return;
      if (value1 !== 0 && value2 !== 0) {
         subTotal = (value1 - value2) / value2;
      }
      return Math.round(subTotal * 100);
   },

   redX() {
      return (
         <span className="text-danger">
            <FontAwesomeIcon icon="times-circle" />
         </span>
      );
   },

   requiredStar() {
      return <span style={{ color: "red" }}>*</span>;
   },

   requiredStar2() {
      return <span style={{ color: "orange" }}>*</span>;
   },

   resolveRoleLabel(role) {
      return constants.ROLE_DICTIONARY[role.TypeOfUserRole];
   },

   roundNum(num, fractionalDigits = 0) {
      const x = parseFloat(num);
      return isNaN(x) ? "" : x.toFixed(fractionalDigits);
   },

   roundToNearestFourth(decimalValue) {
      var wholeNumber = Math.floor(decimalValue);
      const fractionalPart = decimalValue - wholeNumber;
      var nearestFourth = 0.25 * Math.round(fractionalPart / 0.25);

      if (nearestFourth === 0.75) {
         nearestFourth = 0.0;
         wholeNumber += 1;
      }
      const roundedValue = wholeNumber + nearestFourth;
      return roundedValue;
   },

   roundToNearestWholeNumber(num) {
      const x = parseFloat(num);
      if (isNaN(x) || x === 0) return "";
      if (x - Math.floor(x) >= 0.5) {
         return Math.ceil(x);
      } else {
         return Math.floor(x);
      }
   },

   startsWithNumber(str) {
      if (!str) return false;
      return /^\d/.test(str);
   },

   textContains(text, searchText) {
      // Does the string contain the requested text
      return text && -1 !== text.toLowerCase().indexOf(searchText.toLowerCase());
   },

   validateLineItemQty(newQty, minQty, baseUnit) {
      //return parseInt(newQty, 10) >= parseInt(minQty, 10);
      let val = parseInt(newQty, 10);
      let min = parseInt(minQty, 10);
      let base = parseInt(baseUnit, 10) || 1;

      // Was the minimum order quantity met
      if (val < min) return false;

      // If quantity exceeds the MOQ and the base unit is not 1
      // then the quantity must be an increment of the base unit
      if (val > min && base > 1) return helpers.isMultiple(val, base);

      // Quantity is equal to the MOQ or is a valid larger amount
      return true;
   },

   validateNumber(event, precision = 2) {
      const { value } = event.target;
      // Simplified regex assuming a number always starts with at least one digit
      const regex = new RegExp(`^[+-]?[0-9]+(\\.[0-9]{0,${precision}})?$`);
      const found = value.match(regex);
      if (found === null) {
         return false;
      } else {
         return true;
      }
   },

   valueFromSelect(option, defaultToString = false) {
      let value = "";
      if (!option || !option.label) return "";
      value = option.value;
      if (value === 0 && defaultToString) value = "";
      return value;
   },

   valuelabelFromSelect(option, defaultToString = false) {
      let value = 0;
      if (!option || !option.label) return 0;
      value = option.label;
      return value;
   }
};
export default helpers;
